import { WifiOff, RefreshCw, CloudUpload, AlertTriangle, X } from "lucide-react";
import { useOffline } from "@/lib/offline";

/* Staff-side failsafe strip: offline → "cached board"; back online → sync progress; server rejections. */
export default function OfflineBanner() {
  const { online, cached, syncing, pending, failed, drain, clearFailed } = useOffline();
  const showOffline = !online || cached;
  if (!showOffline && pending.length === 0 && failed.length === 0) return null;

  return (
    <div data-testid="offline-banner" className="shrink-0">
      {showOffline && (
        <div className="flex items-center gap-3 px-4 py-2 bg-amber-500 text-black" data-testid="offline-banner-offline">
          <WifiOff className="w-4 h-4 shrink-0 animate-pulse" />
          <div className="mono text-[11px] uppercase tracking-widest font-black">
            {online ? "Reconnecting" : "Offline"} · showing cached board
          </div>
          <div className="text-xs font-medium hidden sm:block">
            Jobs, trucks and dockets are the last copy saved on this device. Status timestamps and notes you enter are kept locally and stamped with the real time.
          </div>
          <div className="ml-auto mono text-[11px] uppercase tracking-widest font-black" data-testid="offline-pending-count">
            {pending.length} pending
          </div>
        </div>
      )}
      {!showOffline && pending.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-1.5 bg-sky-600 text-white" data-testid="offline-banner-syncing">
          {syncing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CloudUpload className="w-4 h-4" />}
          <div className="mono text-[11px] uppercase tracking-widest font-black">
            Back online · {syncing ? "syncing" : "waiting to sync"} <span data-testid="offline-pending-count">{pending.length}</span> offline action{pending.length > 1 ? "s" : ""}
          </div>
          <button type="button" onClick={drain} data-testid="offline-sync-now" disabled={syncing}
            className="ml-auto mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-white/70 hover:bg-white hover:text-sky-700 disabled:opacity-50">
            Sync now
          </button>
        </div>
      )}
      {failed.length > 0 && (
        <div className="flex items-start gap-3 px-4 py-1.5 bg-red-700 text-white" data-testid="offline-banner-failed">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="text-xs">
            <span className="mono uppercase tracking-widest font-black">{failed.length} offline action{failed.length > 1 ? "s" : ""} rejected by the server</span>
            <ul className="mt-0.5 space-y-0.5">
              {failed.slice(0, 3).map((f) => (
                <li key={f.id} className="mono text-[10px]" data-testid="offline-failed-item">{f.label} — {f.error}</li>
              ))}
            </ul>
          </div>
          <button type="button" onClick={clearFailed} data-testid="offline-failed-dismiss" className="ml-auto p-1 hover:bg-white/20" aria-label="Dismiss"><X className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}
